import { CheckCircle2, AlertTriangle, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { PageHeader } from "@/components/admin/PageHeader";
import { ResourceState } from "@/components/admin/ResourceState";
import { useSeoAnalytics } from "@/application/hooks/use-seo-analytics";
import { useI18n } from "@/i18n";

export function SeoPage() {
  const { t } = useI18n();
  const { status, data, error, reload } = useSeoAnalytics();

  return (
    <div>
      <PageHeader title={t.admin.seo.title} description={t.admin.seo.description} />
      <ResourceState status={status} error={error} onRetry={() => void reload()}>
        {data ? (
          <>
            <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
              {[
                { label: t.admin.seo.indexedPages, value: "142", status: "good" },
                { label: t.admin.seo.brokenLinks, value: "3", status: "warn" },
                { label: t.admin.seo.sitemap, value: t.admin.seo.ok, status: "good" },
                {
                  label: t.admin.seo.structuredData,
                  value: t.admin.seo.valid,
                  status: "good",
                },
              ].map((s) => (
                <div
                  key={s.label}
                  className="rounded-2xl border border-border/60 bg-surface-elevated/60 p-4 backdrop-blur"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {s.label}
                    </span>
                    {s.status === "good" ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-amber-400" />
                    )}
                  </div>
                  <div className="mt-2 font-display text-2xl font-semibold">{s.value}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="rounded-2xl border border-border/60 bg-surface-elevated/60 p-5 backdrop-blur lg:col-span-2">
                <h3 className="mb-4 font-display text-base font-semibold">
                  {t.admin.seo.keywordRankings}
                </h3>
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                    <tr className="border-b border-border/60">
                      <th className="pb-2 text-left font-medium">{t.admin.seo.keyword}</th>
                      <th className="pb-2 text-left font-medium">{t.admin.seo.position}</th>
                      <th className="pb-2 text-left font-medium">{t.admin.seo.volume}</th>
                      <th className="pb-2 text-left font-medium">{t.admin.seo.change}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/60">
                    {data.keywordRankings.map((k) => (
                      <tr key={k.keyword}>
                        <td className="py-2.5 font-medium">{k.keyword}</td>
                        <td className="py-2.5 tabular-nums">#{k.position}</td>
                        <td className="py-2.5 tabular-nums text-muted-foreground">
                          {k.volume.toLocaleString()}
                        </td>
                        <td className="py-2.5">
                          <span
                            className={`inline-flex items-center gap-1 text-xs font-semibold ${
                              k.change > 0
                                ? "text-emerald-400"
                                : k.change < 0
                                  ? "text-rose-400"
                                  : "text-muted-foreground"
                            }`}
                          >
                            {k.change > 0 ? (
                              <TrendingUp className="h-3 w-3" />
                            ) : k.change < 0 ? (
                              <TrendingDown className="h-3 w-3" />
                            ) : (
                              <Minus className="h-3 w-3" />
                            )}
                            {k.change > 0 ? `+${k.change}` : k.change}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="rounded-2xl border border-border/60 bg-surface-elevated/60 p-5 backdrop-blur">
                <h3 className="mb-4 font-display text-base font-semibold">
                  {t.admin.seo.coreWebVitals}
                </h3>
                <div className="space-y-3">
                  {Object.entries(data.coreWebVitals).map(([k, v]) => (
                    <div
                      key={k}
                      className="flex items-center justify-between rounded-lg border border-border/60 bg-background/40 px-3 py-2"
                    >
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {k}
                      </span>
                      <span className="font-display text-lg font-semibold tabular-nums">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </ResourceState>
    </div>
  );
}
